import React, { useState, useEffect } from 'react';
import {
  User, Mail, Phone, MapPin, CreditCard, Building2,
  Shield, CheckCircle, AlertCircle, Loader, ChevronRight,
  ChevronLeft, Lock, FileText, Briefcase, Users, DollarSign,
  Eye, EyeOff, Check, X, Beaker, LogIn
} from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
console.log('[BrokerOnboarding] Google Maps API Key:', GOOGLE_MAPS_API_KEY ? 'LOADED' : 'MISSING');
// ============================================
// MOBILE-OPTIMIZED STEP INDICATOR
// Fixed: No overlap, horizontal scroll on small screens
// ============================================
const StepIndicator = ({ currentStep, steps }) => {
  return (
    <div style={{
      width: '100%',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      marginBottom: '1.5rem',
      paddingBottom: '0.5rem'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '0.25rem',
        minWidth: 'max-content',
        padding: '0 1rem'
      }}>
        {steps.map((step, idx) => (
          <React.Fragment key={idx}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.375rem',
              flexShrink: 0,
              minWidth: '50px',
              maxWidth: '70px'
            }}>
              {/* Step Circle */}
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: idx < currentStep 
                  ? '#22c55e' 
                  : idx === currentStep 
                    ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                    : '#334155',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '700',
                fontSize: '0.8rem',
                border: idx === currentStep ? '2px solid #818cf8' : 'none',
                flexShrink: 0
              }}>
                {idx < currentStep ? <Check size={16} /> : idx + 1}
              </div>
              {/* Step Label */}
              <span style={{
                fontSize: '0.65rem',
                color: idx <= currentStep ? '#e2e8f0' : '#64748b',
                fontWeight: idx === currentStep ? '600' : '400',
                textAlign: 'center',
                lineHeight: '1.2',
                wordBreak: 'break-word',
                hyphens: 'auto'
              }}>
                {step}
              </span>
            </div>
            {/* Connector Line */}
            {idx < steps.length - 1 && (
              <div style={{
                width: '24px',
                height: '2px',
                background: idx < currentStep ? '#22c55e' : '#334155',
                marginTop: '15px',
                flexShrink: 0
              }} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ============================================
// INPUT COMPONENT (Mobile Optimized)
// FIXED: Added proper box-sizing for date inputs
// ============================================
const Input = ({ label, type = 'text', value, onChange, placeholder, required, icon: Icon, error, ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const isDate = type === 'date';
  
  return (
    <div style={{ marginBottom: '1rem', width: '100%', boxSizing: 'border-box' }}>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: '#94a3b8',
        fontSize: '0.85rem',
        marginBottom: '0.5rem',
        fontWeight: '500'
      }}>
        {Icon && <Icon size={16} />}
        {label}
        {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
        <input
          type={isPassword && showPassword ? 'text' : type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '0.75rem 1rem',
            paddingRight: isPassword ? '3rem' : '1rem',
            background: '#1e293b',
            border: error ? '2px solid #ef4444' : '1px solid #475569',
            borderRadius: '0.5rem',
            color: '#e2e8f0',
            fontSize: '16px', // Prevents zoom on iOS
            outline: 'none',
            transition: 'border-color 0.2s',
            boxSizing: 'border-box', // FIXED: Ensures padding doesn't cause overflow
            maxWidth: '100%', // FIXED: Prevents overflow
            minHeight: isDate ? '48px' : 'auto', // FIXED: Consistent height for date picker
            WebkitAppearance: 'none', // FIXED: Better cross-browser date input styling
            MozAppearance: 'none',
            appearance: 'none'
          }}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      {error && (
        <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
          {error}
        </p>
      )}
    </div>
  );
};

// ============================================
// SELECT COMPONENT (Mobile Optimized)
// ============================================
const Select = ({ label, value, onChange, options, required, icon: Icon }) => (
  <div style={{ marginBottom: '1rem', width: '100%', boxSizing: 'border-box' }}>
    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#94a3b8',
      fontSize: '0.85rem',
      marginBottom: '0.5rem',
      fontWeight: '500'
    }}>
      {Icon && <Icon size={16} />}
      {label}
      {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
    <select
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '0.75rem 1rem',
        background: '#1e293b',
        border: '1px solid #475569',
        borderRadius: '0.5rem',
        color: '#e2e8f0',
        fontSize: '16px', // Prevents zoom on iOS
        boxSizing: 'border-box',
        maxWidth: '100%'
      }}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// ============================================
// CHECKBOX COMPONENT (Mobile Optimized)
// ============================================
const Checkbox = ({ checked, onChange, label, required }) => (
  <label style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    cursor: 'pointer',
    marginBottom: '1rem',
    WebkitTapHighlightColor: 'transparent'
  }}>
    <div style={{
      width: '24px',
      height: '24px',
      minWidth: '24px',
      borderRadius: '4px',
      border: checked ? 'none' : '2px solid #475569',
      background: checked ? '#22c55e' : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: '2px'
    }}>
      {checked && <Check size={14} color="white" />}
    </div>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ display: 'none' }}
    />
    <span style={{ color: '#cbd5e1', fontSize: '0.9rem', lineHeight: '1.5' }}>
      {label}
      {required && <span style={{ color: '#ef4444' }}> *</span>}
    </span>
  </label>
);

// ============================================
// LOGIN REQUIRED SCREEN
// ============================================
const LoginRequiredScreen = ({ onLoginClick }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.95)',
    borderRadius: '1rem',
    padding: '2rem 1.5rem',
    maxWidth: '500px',
    margin: '0 auto',
    textAlign: 'center'
  }}>
    <div style={{
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem'
    }}>
      <LogIn size={40} color="white" />
    </div>
    
    <h2 style={{ color: '#e2e8f0', margin: '0 0 0.5rem', fontSize: '1.5rem' }}>
      Login Required
    </h2>
    
    <p style={{ color: '#94a3b8', marginBottom: '1.5rem', lineHeight: '1.6' }}>
      Please log in to your InvestorQ account before opening a trading account. 
      This helps us keep your information secure and linked to your profile.
    </p>
    
    <div style={{
      background: 'rgba(96, 165, 250, 0.1)',
      border: '1px solid rgba(96, 165, 250, 0.3)',
      borderRadius: '0.75rem',
      padding: '1rem',
      marginBottom: '1.5rem',
      textAlign: 'left'
    }}>
      <p style={{ color: '#93c5fd', margin: 0, fontSize: '0.9rem' }}>
        <strong>Why do I need to log in?</strong>
      </p>
      <ul style={{ color: '#94a3b8', margin: '0.5rem 0 0', paddingLeft: '1.25rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
        <li>Your trading account will be linked to your InvestorQ profile</li>
        <li>We need to verify your identity for regulatory compliance</li>
        <li>Your portfolio and trades will be saved to your account</li>
        <li>Protects against unauthorized account creation</li>
      </ul>
    </div>
    
    <button
      onClick={onLoginClick}
      style={{
        width: '100%',
        padding: '1rem',
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        border: 'none',
        borderRadius: '0.5rem',
        color: 'white',
        fontWeight: '700',
        fontSize: '1rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem'
      }}
    >
      <LogIn size={20} />
      Log In to Continue
    </button>
    
    <p style={{ color: '#64748b', margin: '1rem 0 0', fontSize: '0.8rem' }}>
      Don't have an account?{' '}
      <a href="/signup" style={{ color: '#818cf8', textDecoration: 'none' }}>
        Sign up for free
      </a>
    </p>
  </div>
);

// ============================================
// MAIN ONBOARDING COMPONENT
// FIXED: Added login requirement check
// ============================================
const BrokerOnboarding = ({ userId, userEmail, onComplete, onCancel, onLoginRequired }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Check if user is logged in - STRICT CHECK
  useEffect(() => {
    const checkAuth = () => {
      // MUST have investorq_user in localStorage with a valid ID
      const storedUser = localStorage.getItem('investorq_user');
      
      console.log('[BrokerOnboarding] Auth check - storedUser:', storedUser ? 'exists' : 'null');
      console.log('[BrokerOnboarding] Auth check - userId prop:', userId);
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          // Verify we have a valid user ID (must be truthy, not null/undefined/empty)
          if (parsedUser && parsedUser.id && String(parsedUser.id).trim() !== '') {
            console.log('[BrokerOnboarding] Auth SUCCESS - User ID:', parsedUser.id);
            setIsLoggedIn(true);
          } else {
            console.log('[BrokerOnboarding] Auth FAILED - Invalid user data in localStorage');
            setIsLoggedIn(false);
          }
        } catch (e) {
          console.error('[BrokerOnboarding] Auth FAILED - Error parsing user data:', e);
          setIsLoggedIn(false);
        }
      } else {
        // No localStorage data - NOT logged in (don't trust just the prop)
        console.log('[BrokerOnboarding] Auth FAILED - No investorq_user in localStorage');
        setIsLoggedIn(false);
      }
      
      setCheckingAuth(false);
    };
    
    checkAuth();
  }, [userId]);
  
  // Get actual user data from localStorage if not passed as props
  const getActualUserId = () => {
    if (userId) return userId;
    
    const storedUser = localStorage.getItem('investorq_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser.id;
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  
  const getActualUserEmail = () => {
    if (userEmail) return userEmail;
    
    const storedUser = localStorage.getItem('investorq_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser.email || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  };
  
  // Form data - pre-fill email if available
  const [formData, setFormData] = useState({
    // Contact
    email: '',
    phone: '',
    streetAddress: '',
    unit: '',
    city: '',
    state: '',
    postalCode: '',
    
    // Identity
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    ssn: '',
    citizenship: 'USA',
    
    // Employment
    employmentStatus: 'employed',
    employerName: '',
    employmentPosition: '',
    fundingSource: 'employment_income',
    
    // Disclosures
    isControlPerson: false,
    isAffiliatedFinra: false,
    isPoliticallyExposed: false,
    familyExposed: false,
    
    // Trusted Contact (optional)
    trustedFirstName: '',
    trustedLastName: '',
    trustedEmail: '',
    trustedPhone: '',
    
    // Agreements
    agreedToInvestorQTerms: false,
    agreedToInvestorQPrivacy: false,
    agreedToAlpacaCustomer: false,
    agreedToAlpacaAccount: false,
    agreedToPlaid: false,
    agreedToRisk: false
  });
  
  // Pre-fill email when user data is available
  useEffect(() => {
    const email = getActualUserEmail();
    if (email && !formData.email) {
      setFormData(prev => ({ ...prev, email }));
    }
  }, [isLoggedIn]);

  const steps = ['Personal', 'Address', 'Employment', 'Disclosures', 'Review'];

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Personal Info
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
          setError('Please fill in all required fields');
          return false;
        }
        if (!formData.dateOfBirth) {
          setError('Please enter your date of birth');
          return false;
        }
        if (!formData.ssn || formData.ssn.replace(/-/g, '').length !== 9) {
          setError('Please enter a valid SSN');
          return false;
        }
        // Age check (must be 18+)
        const birthDate = new Date(formData.dateOfBirth);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 18) {
          setError('You must be at least 18 years old to open an account');
          return false;
        }
        return true;
        
      case 1: // Address
        if (!formData.streetAddress || !formData.city || !formData.state || !formData.postalCode) {
          setError('Please fill in all required address fields');
          return false;
        }
        return true;
        
      case 2: // Employment
        if (!formData.employmentStatus || !formData.fundingSource) {
          setError('Please complete employment information');
          return false;
        }
        if (formData.employmentStatus === 'employed' && !formData.employerName) {
          setError('Please enter your employer name');
          return false;
        }
        return true;
        
      case 3: // Disclosures
        return true; // No required fields
        
      case 4: // Review & Agreements
        if (!formData.agreedToInvestorQTerms || !formData.agreedToInvestorQPrivacy || 
            !formData.agreedToAlpacaCustomer || !formData.agreedToAlpacaAccount || 
            !formData.agreedToPlaid || !formData.agreedToRisk) {
          setError('You must agree to all terms and agreements to continue');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    const actualUserId = getActualUserId();
    if (!actualUserId) {
      setError('User session expired. Please log in again.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/broker/account/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: actualUserId,
          contact: {
            email: formData.email,
            phone: formData.phone,
            streetAddress: formData.streetAddress,
            unit: formData.unit,
            city: formData.city,
            state: formData.state,
            postalCode: formData.postalCode
          },
          identity: {
            firstName: formData.firstName,
            middleName: formData.middleName,
            lastName: formData.lastName,
            dateOfBirth: formData.dateOfBirth,
            ssn: formData.ssn,
            citizenship: formData.citizenship,
            fundingSource: [formData.fundingSource]
          },
          disclosures: {
            isControlPerson: formData.isControlPerson,
            isAffiliatedFinra: formData.isAffiliatedFinra,
            isPoliticallyExposed: formData.isPoliticallyExposed,
            familyExposed: formData.familyExposed,
            employmentStatus: formData.employmentStatus,
            employerName: formData.employerName,
            employmentPosition: formData.employmentPosition
          },
          trustedContact: formData.trustedFirstName ? {
            firstName: formData.trustedFirstName,
            lastName: formData.trustedLastName,
            email: formData.trustedEmail,
            phone: formData.trustedPhone
          } : null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        if (onComplete) onComplete(data.account);
      } else {
        setError(data.error || 'Failed to create account');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle login redirect
  const handleLoginClick = () => {
    if (onLoginRequired) {
      onLoginRequired();
    } else {
      // Default: redirect to login page
      window.location.href = '/login?redirect=trading';
    }
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px'
      }}>
        <Loader size={32} color="#6366f1" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }
  
  // Show login required screen if not logged in
  if (!isLoggedIn) {
    return <LoginRequiredScreen onLoginClick={handleLoginClick} />;
  }

  // Success screen
  if (success) {
    return (
      <div style={{
        background: 'rgba(30, 41, 59, 0.95)',
        borderRadius: '1rem',
        padding: '2rem 1.5rem',
        maxWidth: '500px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <CheckCircle size={40} color="white" />
        </div>
        
        <h2 style={{ color: '#e2e8f0', margin: '0 0 0.5rem', fontSize: '1.5rem' }}>
          Application Submitted! 🎉
        </h2>
        
        <p style={{ color: '#94a3b8', marginBottom: '1.5rem', lineHeight: '1.6' }}>
          Your brokerage account application has been submitted for review.
          You'll receive an email once your account is approved, typically within 1-3 business days.
        </p>
        
        <div style={{
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '0.75rem',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <p style={{ color: '#fcd34d', margin: 0, fontSize: '0.9rem' }}>
            <strong>What's Next?</strong><br />
            Once approved, you can link your bank account and start trading!
          </p>
        </div>
        
        <button
          onClick={onComplete}
          style={{
            width: '100%',
            padding: '1rem',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            fontWeight: '700',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          Got it!
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.95)',
      borderRadius: '1rem',
      padding: '1.5rem 1rem',
      maxWidth: '600px',
      margin: '0 auto',
      boxSizing: 'border-box',
      overflow: 'hidden' // FIXED: Prevents any child overflow
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <h2 style={{ 
          color: '#e2e8f0', 
          margin: '0 0 0.5rem',
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}>
          <Shield size={24} color="#6366f1" />
          Open Trading Account
        </h2>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.85rem' }}>
          Start trading stocks commission-free
        </p>
      </div>

      {/* Sandbox Mode Notice */}
      <div style={{
        background: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '0.5rem',
        padding: '0.75rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Beaker size={18} color="#22c55e" style={{ flexShrink: 0 }} />
        <span style={{ color: '#86efac', fontSize: '0.8rem' }}>
          🧪 <strong>Sandbox Mode:</strong> Use any fake data for testing (SSN: 123-45-6789)
        </span>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} steps={steps} />

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '0.5rem',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertCircle size={18} color="#ef4444" style={{ flexShrink: 0 }} />
          <span style={{ color: '#ef4444', fontSize: '0.9rem', flex: 1 }}>{error}</span>
        </div>
      )}

      {/* Form Container - FIXED: Added overflow handling */}
      <div style={{ 
        width: '100%', 
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        {/* Step 1: Personal Info */}
        {currentStep === 0 && (
          <div>
            <h3 style={{ color: '#e2e8f0', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <User size={20} />
              Personal Information
            </h3>
            
            {/* Mobile: Stack, Desktop: 2 columns */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
              gap: '0.75rem' 
            }}>
              <Input
                label="First Name"
                value={formData.firstName}
                onChange={(e) => updateForm('firstName', e.target.value)}
                placeholder="John"
                required
              />
              <Input
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => updateForm('lastName', e.target.value)}
                placeholder="Doe"
                required
              />
            </div>
            
            <Input
              label="Middle Name"
              value={formData.middleName}
              onChange={(e) => updateForm('middleName', e.target.value)}
              placeholder="(Optional)"
            />
            
            <Input
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => updateForm('email', e.target.value)}
              placeholder="john@example.com"
              icon={Mail}
              required
            />
            
            <Input
              label="Phone Number"
              type="tel"
              value={formData.phone}
              onChange={(e) => updateForm('phone', e.target.value)}
              placeholder="555-123-4567"
              icon={Phone}
              required
            />
            
            {/* FIXED: Date of Birth with proper container */}
            <div style={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
              <Input
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => updateForm('dateOfBirth', e.target.value)}
                required
              />
            </div>
            
            <Input
              label="Social Security Number"
              value={formData.ssn}
              onChange={(e) => {
                // Format SSN as XXX-XX-XXXX
                let val = e.target.value.replace(/\D/g, '');
                if (val.length > 3) val = val.slice(0, 3) + '-' + val.slice(3);
                if (val.length > 6) val = val.slice(0, 6) + '-' + val.slice(6, 10);
                updateForm('ssn', val);
              }}
              placeholder="123-45-6789"
              icon={Lock}
              required
              maxLength={11}
            />
            
            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '0.5rem',
              padding: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem'
            }}>
              <Lock size={16} color="#22c55e" style={{ flexShrink: 0 }} />
              <span style={{ color: '#86efac', fontSize: '0.8rem' }}>
                Your SSN is encrypted and securely stored. Required by law for brokerage accounts.
              </span>
            </div>
          </div>
        )}

        {/* Step 2: Address */}
        {currentStep === 1 && (
          <div>
            <h3 style={{ color: '#e2e8f0', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <MapPin size={20} />
              Residential Address
            </h3>
            
{/* Address Autocomplete */}
{GOOGLE_MAPS_API_KEY && (
  <>
    <AddressAutocomplete
      googleMapsApiKey={GOOGLE_MAPS_API_KEY}
      onAddressSelect={(address) => {
        updateForm('streetAddress', address.streetAddress);
        updateForm('unit', address.unit || '');
        updateForm('city', address.city);
        updateForm('state', address.state);
        updateForm('postalCode', address.postalCode);
      }}
      placeholder="Start typing your address..."
    />
    <div style={{
      display: 'flex', alignItems: 'center', gap: '1rem',
      margin: '1rem 0', color: '#64748b', fontSize: '0.8rem'
    }}>
      <div style={{ flex: 1, height: '1px', background: '#334155' }} />
      <span>or enter manually</span>
      <div style={{ flex: 1, height: '1px', background: '#334155' }} />
    </div>
  </>
)}

            <Input
              label="Street Address"
              value={formData.streetAddress}
              onChange={(e) => updateForm('streetAddress', e.target.value)}
              placeholder="123 Main Street"
              required
            />
            
            <Input
              label="Apt/Unit"
              value={formData.unit}
              onChange={(e) => updateForm('unit', e.target.value)}
              placeholder="(Optional)"
            />
            
            <Input
              label="City"
              value={formData.city}
              onChange={(e) => updateForm('city', e.target.value)}
              placeholder="New York"
              required
            />
            
            {/* Mobile: Stack State & ZIP */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
              gap: '0.75rem' 
            }}>
              <Select
                label="State"
                value={formData.state}
                onChange={(e) => updateForm('state', e.target.value)}
                options={[
                  { value: '', label: 'Select' },
                  { value: 'AL', label: 'AL' }, { value: 'AK', label: 'AK' },
                  { value: 'AZ', label: 'AZ' }, { value: 'AR', label: 'AR' },
                  { value: 'CA', label: 'CA' }, { value: 'CO', label: 'CO' },
                  { value: 'CT', label: 'CT' }, { value: 'DE', label: 'DE' },
                  { value: 'FL', label: 'FL' }, { value: 'GA', label: 'GA' },
                  { value: 'HI', label: 'HI' }, { value: 'ID', label: 'ID' },
                  { value: 'IL', label: 'IL' }, { value: 'IN', label: 'IN' },
                  { value: 'IA', label: 'IA' }, { value: 'KS', label: 'KS' },
                  { value: 'KY', label: 'KY' }, { value: 'LA', label: 'LA' },
                  { value: 'ME', label: 'ME' }, { value: 'MD', label: 'MD' },
                  { value: 'MA', label: 'MA' }, { value: 'MI', label: 'MI' },
                  { value: 'MN', label: 'MN' }, { value: 'MS', label: 'MS' },
                  { value: 'MO', label: 'MO' }, { value: 'MT', label: 'MT' },
                  { value: 'NE', label: 'NE' }, { value: 'NV', label: 'NV' },
                  { value: 'NH', label: 'NH' }, { value: 'NJ', label: 'NJ' },
                  { value: 'NM', label: 'NM' }, { value: 'NY', label: 'NY' },
                  { value: 'NC', label: 'NC' }, { value: 'ND', label: 'ND' },
                  { value: 'OH', label: 'OH' }, { value: 'OK', label: 'OK' },
                  { value: 'OR', label: 'OR' }, { value: 'PA', label: 'PA' },
                  { value: 'RI', label: 'RI' }, { value: 'SC', label: 'SC' },
                  { value: 'SD', label: 'SD' }, { value: 'TN', label: 'TN' },
                  { value: 'TX', label: 'TX' }, { value: 'UT', label: 'UT' },
                  { value: 'VT', label: 'VT' }, { value: 'VA', label: 'VA' },
                  { value: 'WA', label: 'WA' }, { value: 'WV', label: 'WV' },
                  { value: 'WI', label: 'WI' }, { value: 'WY', label: 'WY' },
                  { value: 'DC', label: 'DC' }
                ]}
                required
              />
              <Input
                label="ZIP Code"
                value={formData.postalCode}
                onChange={(e) => updateForm('postalCode', e.target.value)}
                placeholder="10001"
                required
                maxLength={5}
              />
            </div>
          </div>
        )}

        {/* Step 3: Employment */}
        {currentStep === 2 && (
          <div>
            <h3 style={{ color: '#e2e8f0', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <Briefcase size={20} />
              Employment Information
            </h3>
            
            <Select
              label="Employment Status"
              value={formData.employmentStatus}
              onChange={(e) => updateForm('employmentStatus', e.target.value)}
              options={[
                { value: 'employed', label: 'Employed' },
                { value: 'self_employed', label: 'Self-Employed' },
                { value: 'unemployed', label: 'Unemployed' },
                { value: 'retired', label: 'Retired' },
                { value: 'student', label: 'Student' }
              ]}
              icon={Briefcase}
              required
            />
            
            {(formData.employmentStatus === 'employed' || formData.employmentStatus === 'self_employed') && (
              <>
                <Input
                  label="Employer Name"
                  value={formData.employerName}
                  onChange={(e) => updateForm('employerName', e.target.value)}
                  placeholder="Company Inc."
                  icon={Building2}
                  required
                />
                <Input
                  label="Job Title"
                  value={formData.employmentPosition}
                  onChange={(e) => updateForm('employmentPosition', e.target.value)}
                  placeholder="Software Engineer"
                />
              </>
            )}
            
            <Select
              label="Primary Source of Funds"
              value={formData.fundingSource}
              onChange={(e) => updateForm('fundingSource', e.target.value)}
              options={[
                { value: 'employment_income', label: 'Employment Income' },
                { value: 'investments', label: 'Investments' },
                { value: 'inheritance', label: 'Inheritance' },
                { value: 'business_income', label: 'Business Income' },
                { value: 'savings', label: 'Savings' },
                { value: 'family', label: 'Family' }
              ]}
              icon={DollarSign}
              required
            />
          </div>
        )}

        {/* Step 4: Disclosures */}
        {currentStep === 3 && (
          <div>
            <h3 style={{ color: '#e2e8f0', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <FileText size={20} />
              Regulatory Disclosures
            </h3>
            
            <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Please answer the following questions truthfully. These are required by SEC regulations.
            </p>
            
            <Checkbox
              checked={formData.isControlPerson}
              onChange={(e) => updateForm('isControlPerson', e.target.checked)}
              label="I am a director, officer, or 10%+ shareholder of a publicly traded company"
            />
            
            <Checkbox
              checked={formData.isAffiliatedFinra}
              onChange={(e) => updateForm('isAffiliatedFinra', e.target.checked)}
              label="I am affiliated with or employed by a stock exchange or FINRA member firm"
            />
            
            <Checkbox
              checked={formData.isPoliticallyExposed}
              onChange={(e) => updateForm('isPoliticallyExposed', e.target.checked)}
              label="I am a current or former senior political figure"
            />
            
            <Checkbox
              checked={formData.familyExposed}
              onChange={(e) => updateForm('familyExposed', e.target.checked)}
              label="An immediate family member is a senior political figure"
            />
            
            <div style={{
              background: 'rgba(96, 165, 250, 0.1)',
              border: '1px solid rgba(96, 165, 250, 0.3)',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <p style={{ color: '#93c5fd', margin: 0, fontSize: '0.85rem' }}>
                💡 Most people answer "No" to all of these. If any apply to you, additional documentation may be required.
              </p>
            </div>
          </div>
        )}

        {/* Step 5: Review & Submit */}
        {currentStep === 4 && (
          <div>
            <h3 style={{ color: '#e2e8f0', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem' }}>
              <CheckCircle size={20} />
              Review & Submit
            </h3>
            
            {/* Summary */}
            <div style={{
              background: 'rgba(15, 23, 42, 0.6)',
              borderRadius: '0.75rem',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ color: '#94a3b8', margin: '0 0 0.5rem', fontSize: '0.75rem', textTransform: 'uppercase' }}>Account Holder</h4>
              <p style={{ color: '#e2e8f0', margin: 0, fontWeight: '600' }}>
                {formData.firstName} {formData.middleName} {formData.lastName}
              </p>
              <p style={{ color: '#94a3b8', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                {formData.email}
              </p>
              <p style={{ color: '#94a3b8', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                {formData.streetAddress}{formData.unit ? ` ${formData.unit}` : ''}, {formData.city}, {formData.state} {formData.postalCode}
              </p>
            </div>
            
            {/* Agreements */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.85rem', textTransform: 'uppercase' }}>
                InvestorQ Agreements
              </h4>
              
              <Checkbox
                checked={formData.agreedToInvestorQTerms}
                onChange={(e) => updateForm('agreedToInvestorQTerms', e.target.checked)}
                label={
                  <span>
                    I have read and agree to InvestorQ's{' '}
                    <a href="/terms.html" target="_blank" style={{ color: '#818cf8' }}>Terms of Service</a>
                  </span>
                }
                required
              />
              
              <Checkbox
                checked={formData.agreedToInvestorQPrivacy}
                onChange={(e) => updateForm('agreedToInvestorQPrivacy', e.target.checked)}
                label={
                  <span>
                    I have read and agree to InvestorQ's{' '}
                    <a href="/privacy.html" target="_blank" style={{ color: '#818cf8' }}>Privacy Policy</a>
                  </span>
                }
                required
              />
              
              <h4 style={{ color: '#94a3b8', marginBottom: '1rem', marginTop: '1.5rem', fontSize: '0.85rem', textTransform: 'uppercase' }}>
                Brokerage Agreements
              </h4>
              
              <Checkbox
                checked={formData.agreedToAlpacaCustomer}
                onChange={(e) => updateForm('agreedToAlpacaCustomer', e.target.checked)}
                label={
                  <span>
                    I acknowledge that brokerage services are pending broker-dealer go-live and agree to the customer agreement upon launch
                  </span>
                }
                required
              />
              
              <Checkbox
                checked={formData.agreedToAlpacaAccount}
                onChange={(e) => updateForm('agreedToAlpacaAccount', e.target.checked)}
                label={
                  <span>
                    I acknowledge that full account terms and disclosures will be provided by our broker-dealer partner upon go-live
                  </span>
                }
                required
              />
              
              <h4 style={{ color: '#94a3b8', marginBottom: '1rem', marginTop: '1.5rem', fontSize: '0.85rem', textTransform: 'uppercase' }}>
                Bank Linking (Plaid)
              </h4>
              
              <Checkbox
                checked={formData.agreedToPlaid}
                onChange={(e) => updateForm('agreedToPlaid', e.target.checked)}
                label={
                  <span>
                    I have read and agree to{' '}
                    <a href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" style={{ color: '#818cf8' }}>Plaid's Privacy Policy</a> and authorize Plaid to connect my bank account for funding
                  </span>
                }
                required
              />
              
              <h4 style={{ color: '#94a3b8', marginBottom: '1rem', marginTop: '1.5rem', fontSize: '0.85rem', textTransform: 'uppercase' }}>
                Risk Acknowledgment
              </h4>
              
              <Checkbox
                checked={formData.agreedToRisk}
                onChange={(e) => updateForm('agreedToRisk', e.target.checked)}
                label="I understand that investing involves risk, including the possible loss of principal, and that past performance does not guarantee future results"
                required
              />
            </div>
            
            {/* Disclosure */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.6)',
              borderRadius: '0.5rem',
              padding: '1rem',
              fontSize: '0.75rem',
              color: '#64748b',
              lineHeight: '1.6'
            }}>
              <Shield size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Brokerage services — broker-dealer partnership in progress. Upon go-live, securities in your account will be protected by SIPC up to $500,000. InvestorQ is not a broker-dealer or investment advisor.
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '1.5rem',
        gap: '1rem'
      }}>
        {currentStep > 0 ? (
          <button
            onClick={prevStep}
            style={{
              padding: '0.875rem 1.25rem',
              background: '#334155',
              border: 'none',
              borderRadius: '0.5rem',
              color: '#e2e8f0',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem'
            }}
          >
            <ChevronLeft size={18} />
            Back
          </button>
        ) : (
          <button
            onClick={onCancel}
            style={{
              padding: '0.875rem 1.25rem',
              background: 'transparent',
              border: '1px solid #475569',
              borderRadius: '0.5rem',
              color: '#94a3b8',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Cancel
          </button>
        )}
        
        {currentStep < steps.length - 1 ? (
          <button
            onClick={nextStep}
            style={{
              padding: '0.875rem 1.25rem',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem'
            }}
          >
            Continue
            <ChevronRight size={18} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '0.875rem 1.5rem',
              background: loading ? '#475569' : 'linear-gradient(135deg, #22c55e, #16a34a)',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem'
            }}
          >
            {loading ? (
              <>
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Submitting...
              </>
            ) : (
              <>
                <Shield size={18} />
                Submit
              </>
            )}
          </button>
        )}
      </div>
      
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BrokerOnboarding;